import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { BreakoutUser } from '../models/BreakoutUser'
import { SectionAssignment } from '../models/SectionAssignment'
import { Section } from '../models/Section'
import {
  ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT,
  RoomState,
  RoomStateStatus,
} from '../models/RoomState'
import { SlideDeck } from '../models/SlideDeck'
import { LibraryObjectState, UserProfileRole } from '../types'
import { computed, makeObservable } from 'mobx'
import { DateTime } from 'luxon'
import { assert } from '../firestore-mobx/utils'
import { SectionState } from '../firestore/Section'
import { type SlideQuestion } from '../models/SlideQuestion'
import { type RoomStateAnswer } from '../models/RoomStateAnswer'
import { type StudentLibraryCubit } from '../cubits/StudentLibraryCubit'
import { type SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'

export class ValidLibraryObject {
  repository: FirebaseRepository
  user: BreakoutUser
  private cubitData:
    | {
        cubit: StudentLibraryCubit
        sectionId: string
        assignmentId: string
        roomId?: string //not always a room associated with an assignment
      }
    | {
        section: Section
        assignment: SectionAssignment
        roomState: RoomState
        slideDeck: SlideDeck
        sectionPassPricing: SettingsSectionPassPricing
        quizData?: {
          slideQuestions: SlideQuestion[]
          userAnswers: RoomStateAnswer[]
        }
        /**  */
        hasGrading: null | boolean
      }

  constructor(
    params:
      | {
          repository: FirebaseRepository
          section: Section
          assignment: SectionAssignment
          roomState: RoomState
          slideDeck: SlideDeck
          quizData?: {
            slideQuestions: SlideQuestion[]
            userAnswers: RoomStateAnswer[]
          }
          sectionPassPricing: SettingsSectionPassPricing
          hasGrading: null | boolean
        }
      | {
          cubit: StudentLibraryCubit
          assignmentId: string
          sectionId: string
          roomId?: string
        }
  ) {
    makeObservable(this)
    const user =
      'cubit' in params
        ? params.cubit.repository.breakoutUser
        : params.repository.breakoutUser
    const repository =
      'cubit' in params ? params.cubit.repository : params.repository
    assert(user, 'User must be logged in')
    this.user = user
    this.repository = repository
    this.cubitData = params
  }

  @computed
  get libraryObjectId() {
    // if there is a roomState, use the roomState id
    if (this.roomState.id) return this.assignment.id + '-' + this.roomState.id
    return this.assignment.id
  }

  @computed
  get slideDeckId() {
    return this.slideDeck.id
  }

  @computed
  get slideDeckName() {
    return this.slideDeck.data.slideDeckName
  }

  @computed
  get slideDeckPrice() {
    return this.slideDeck.data.slideDeckPrice
  }

  @computed
  get slideDeckImageURL() {
    return this.slideDeck.data.slideDeckImageURL
  }

  @computed
  get slideDeckTeaser() {
    return this.slideDeck.data.slideDeckTeaser
  }

  @computed
  get requiresPayment() {
    if (
      this.assignment.isExpired ||
      this.section.data.sectionState === SectionState.completed ||
      (this.section.usesSectionPass &&
        this.user.sectionPassSectionIds.has(this.section.id))
    ) {
      return false
    }
    return !this.hasAccessToSlideDeckContents
  }

  /**
   * we want to return undefined if the roomState is empty or if the roomStateStatus is mustSchedule
   * to drive the student assignment UI without explicitly checking roomStateStatus everywhere
   */
  @computed
  get scheduledAtDate() {
    if (
      this.roomState.isEmpty ||
      this.roomStateStatus === RoomStateStatus.mustScheduleSuspended
    )
      return undefined
    return this.roomState.scheduledAtDate
  }

  /**
   * The priority of room > section > feature flags is observed by the broadcast server
   * if changed here the trigger must also be updated with the new priority
   */
  @computed
  get videoMethod() {
    const { videoMethod: roomVideoMethod } = this.roomState.data
    const { videoMethod: sectionVideoMethod } = this.section.data
    return (
      roomVideoMethod ??
      sectionVideoMethod ??
      this.repository.featureFlags.data.videoMethod
    )
  }

  @computed
  get hasAccessToSlideDeckContents() {
    const matchingTokensCount = this.user.tokens
      .filter((tokenModel) => {
        const token = tokenModel.data
        return (
          (token.consumed === true &&
            token.sectionId !== null &&
            token.assignmentId !== null &&
            token.sectionId === this.section.id &&
            token.assignmentId === this.assignment.id) ||
          (this.roomState.isNotEmpty &&
            token.roomId !== null &&
            token.roomId === this.roomState.id)
        )
      })
      .reduce((previousValue, element) => {
        return previousValue + element.data.tokenQuantity
      }, 0)

    const havePurchaseRecord = this.user.purchases.some(
      (purchase) => purchase.id === this.slideDeck.id
    )

    const result =
      (matchingTokensCount < this.slideDeck.data.slideDeckPrice ||
        !havePurchaseRecord) &&
      this.user.role === UserProfileRole.student &&
      !this.slideDeck.data.slideDeckFree

    return !result
  }

  @computed
  get roomStateStatus() {
    return this.roomState.getRoomStateStatus(
      this.assignment.expiresAt,
      this.section.data.sectionState,
      this.slideDeck.data.slideDeckSlideCount,
      this.assignment.data.assignmentState,
      this._roomHasGrade
    )
  }

  @computed
  get beforeSession() {
    return (
      this.libraryObjectState === LibraryObjectState.enrolled ||
      this.libraryObjectState === LibraryObjectState.invited ||
      this.libraryObjectState === LibraryObjectState.mustSchedule ||
      this.libraryObjectState === LibraryObjectState.pending ||
      this.libraryObjectState === LibraryObjectState.scheduled
    )
  }

  @computed
  get afterSession() {
    return (
      this.libraryObjectState === LibraryObjectState.completed ||
      this.libraryObjectState === LibraryObjectState.expired
    )
  }

  @computed
  get groupLeader() {
    return this.roomState.groupLeaderUserIds.includes(this.user.uid)
  }

  @computed
  get instructor() {
    return this.section.instructor
  }

  @computed
  get libraryObjectState(): LibraryObjectState {
    // reason doesn't matter here, we just need to know if we can schedule
    const canSchedule = this.canScheduleRoom

    const expired =
      this.section.data.sectionState === SectionState.completed ||
      this.assignment.isExpired

    const cancelled = this.roomStateStatus === RoomStateStatus.canceled

    const mustSchedule = [
      RoomStateStatus.mustSchedule,
      RoomStateStatus.mustScheduleSuspended,
    ].includes(this.roomStateStatus)

    // NO ROOM
    if (!this.roomState.id) {
      if (cancelled) return LibraryObjectState.canceled
      if (expired) return LibraryObjectState.expired
      if (this.requiresPayment) return LibraryObjectState.invited
      return LibraryObjectState.enrolled
    }

    // WITH ROOM (did not start)
    if (!this.roomState.didStart) {
      if (cancelled) return LibraryObjectState.canceled
      if (expired) return LibraryObjectState.expired
      if (this.requiresPayment) return LibraryObjectState.invited
      if (this.roomState.isScheduled && !mustSchedule) {
        if (this.canStart) return LibraryObjectState.readyToStart
        return LibraryObjectState.scheduled
      }

      return canSchedule
        ? LibraryObjectState.mustSchedule
        : LibraryObjectState.pending
    }

    // WITH ROOM (did start)
    if (mustSchedule) {
      return canSchedule
        ? LibraryObjectState.mustSchedule
        : LibraryObjectState.pending
    }

    if (this.roomStateStatus === RoomStateStatus.completed) {
      return LibraryObjectState.completed
    }
    if (this.roomStateStatus === RoomStateStatus.suspended) {
      if (!this.roomState.canStart) return LibraryObjectState.scheduled
      return LibraryObjectState.suspended
    }
    if (cancelled) return LibraryObjectState.canceled
    if (expired) return LibraryObjectState.expired
    if (this.requiresPayment) return LibraryObjectState.invited
    return LibraryObjectState.live
  }

  @computed
  get currentUserCanStartRoom() {
    return (
      this.libraryObjectState === LibraryObjectState.readyToStart ||
      (this.libraryObjectState === LibraryObjectState.live &&
        !this.roomState.didStart)
    )
  }

  @computed
  get currentUserCanJoinRoom() {
    return (
      this.libraryObjectState === LibraryObjectState.live &&
      this.roomState.didStart
    )
  }

  @computed
  get roomCanStartTime() {
    const scheduledAt = this.roomState.data.scheduledAt
    if (!scheduledAt) return null
    return DateTime.fromJSDate(scheduledAt).minus({
      minutes: ROOM_CAN_START_MINUTES_BEFORE_SCHEDULED_AT,
    })
  }

  @computed
  get canStart() {
    const scheduledAt = this.roomState.data.scheduledAt
    const roomCanStartTime = this.roomCanStartTime
    if (!scheduledAt || !roomCanStartTime) return false

    return this.repository.currentMinute > roomCanStartTime
  }

  @computed
  // Returns 10 minutes before meeting is scheduled to start, ex: 9/12/24 12:50pm PT
  get canJoinTimeFormatted() {
    const roomCanStartTime = this.roomCanStartTime
    if (!roomCanStartTime) return ''

    return roomCanStartTime.toFormat('M/d/yy hh:mma ZZZZ')
  }

  @computed
  get quizDataRequiredForActionState() {
    // if not completed but is scheduled, we must check if we need to complete the quiz
    return (
      this.roomState.isNotEmpty &&
      this.roomState.isScheduled &&
      !(this.roomStateStatus === RoomStateStatus.completed) &&
      !this.assignment.isExpired &&
      this.section.data.sectionState !== SectionState.completed &&
      this.hasAccessToSlideDeckContents &&
      !this.requiresPayment
    )
  }

  @computed
  get currentUserWasAbsentAndCanRejoin(): boolean {
    const absentUserIds = this.roomState.data.absentUserIds ?? []
    if (!absentUserIds.includes(this.user.uid)) return false
    if (this.assignment.isExpired) return false
    if (this.section.data.sectionState === SectionState.completed) return false
    if (this.assignment.data.forbidStudentGroupSelection) return false
    return true
  }

  getActionState({
    hasOutstandingPeerReview,
  }: {
    hasOutstandingPeerReview: boolean
  }): ValidLibraryObjectActionState {
    if (
      this.assignment.assignedAtIsInFuture &&
      this.libraryObjectState !== LibraryObjectState.canceled
    )
      return ValidLibraryObjectActionState.availableOn

    if (
      this.quizDataRequiredForActionState &&
      this.libraryObjectState !== LibraryObjectState.completed &&
      this.libraryObjectState !== LibraryObjectState.expired &&
      this.libraryObjectState !== LibraryObjectState.invited &&
      this.libraryObjectState !== LibraryObjectState.canceled
    ) {
      if (this.quizData === undefined) {
        return ValidLibraryObjectActionState.loading
      }
      // check if we have answers for all the pre-work quiz questions
      const preMeetingQuizQuestions = this.quizData.slideQuestions.filter(
        (q) => q.isPreMeetingQuestion
      )
      if (
        !preMeetingQuizQuestions.every((question) =>
          this.quizData!.userAnswers.some(
            (answer) => answer.data.slideQuestionId === question.id
          )
        )
      ) {
        return ValidLibraryObjectActionState.completeQuiz
      }
    }

    switch (this.libraryObjectState) {
      case LibraryObjectState.live:
        // if room is not active (but live), we can start it
        if (this.currentUserCanStartRoom)
          return ValidLibraryObjectActionState.startSession
        return ValidLibraryObjectActionState.joinSession
      case LibraryObjectState.readyToStart: {
        if (this.currentUserCanStartRoom)
          return ValidLibraryObjectActionState.startSession
        return ValidLibraryObjectActionState.sessionScheduled
      }
      case LibraryObjectState.invited:
        return ValidLibraryObjectActionState.enroll
      case LibraryObjectState.enrolled: {
        if (this.assignment.data.forbidStudentGroupSelection)
          return ValidLibraryObjectActionState.pendingGrouping
        return ValidLibraryObjectActionState.joinGroup
      }
      case LibraryObjectState.mustSchedule:
        return ValidLibraryObjectActionState.scheduleSession
      case LibraryObjectState.scheduled:
        return ValidLibraryObjectActionState.sessionScheduled
      case LibraryObjectState.pending:
        return ValidLibraryObjectActionState.pendingScheduling
      case LibraryObjectState.completed:
        if (this.currentUserWasAbsentAndCanRejoin) {
          return ValidLibraryObjectActionState.joinNewGroup
        }
        if (
          hasOutstandingPeerReview &&
          this.section.data.sectionState !== SectionState.completed &&
          !this.assignment.isExpired
        ) {
          return ValidLibraryObjectActionState.completePeerReviews
        }
        return ValidLibraryObjectActionState.viewResults
      case LibraryObjectState.expired:
        return ValidLibraryObjectActionState.experienceExpired
      case LibraryObjectState.suspended:
        return ValidLibraryObjectActionState.suspended
      case LibraryObjectState.canceled:
        return ValidLibraryObjectActionState.canceled
    }
  }

  @computed
  private get canScheduleRoom(): boolean {
    // if scheduling is not strictly forbidden, we can schedule
    return !this.assignment.data.forbidStudentGroupScheduling
  }

  @computed
  get sectionPassPriceCents() {
    const { sectionPassPrice: priceFromSection } = this.section.data
    if (priceFromSection) return priceFromSection
    if ('cubit' in this.cubitData)
      return this.cubitData.cubit.sectionPassPricing.sectionPassPriceInt
    return this.cubitData.sectionPassPricing.sectionPassPriceInt
  }

  @computed
  get sectionPassPriceDollars() {
    if (!this.sectionPassPriceCents) return ''
    return (this.sectionPassPriceCents / 100).toFixed(2)
  }

  @computed
  get quizData() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const {
        id,
        data: { slideDeckId },
      } = this.roomState
      const slideQuestions = cubit.slideQuestionsBySlideDeckId.get(slideDeckId)
      const userAnswers = cubit.userAnswersByRoomId.get(id)
      const questionsReady = slideQuestions && slideQuestions.isLoaded
      const answersReady = userAnswers && userAnswers.isLoaded
      if (!questionsReady || !answersReady) return undefined
      return {
        slideQuestions: slideQuestions.models,
        userAnswers: userAnswers.models,
      }
    }
    return this.cubitData.quizData
  }

  @computed
  get section() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const section = cubit.sections.models.find(
        (s) => s.id === this.assignment.data.sectionId
      )
      return section || Section.empty(this.repository)
    }
    return this.cubitData.section
  }

  @computed
  get assignment() {
    if ('cubit' in this.cubitData) {
      const { cubit, assignmentId } = this.cubitData
      const assignment = cubit.allSectionAssignments.find(
        (a) => a.id === assignmentId
      )
      return assignment || SectionAssignment.empty(this.repository)
    }
    return this.cubitData.assignment
  }

  @computed
  get roomState() {
    if ('cubit' in this.cubitData) {
      const { cubit, roomId } = this.cubitData
      const roomState = cubit.roomStates.models.find((r) => r.id === roomId)
      return roomState || RoomState.empty(this.repository)
    }
    return this.cubitData.roomState
  }

  @computed
  get slideDeck() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const slideDeck = cubit.slideDecksById.get(
        this.assignment.data.slideDeckId
      )
      return slideDeck || SlideDeck.empty(this.repository)
    }
    return this.cubitData.slideDeck
  }

  @computed
  /** returns null if unknown true/false if known */
  private get _roomHasGrade() {
    if ('cubit' in this.cubitData) {
      const { cubit } = this.cubitData
      const gradingResult = cubit.roomHasGradingResults.get(this.roomState.id)
      return gradingResult === undefined ? null : gradingResult
    }
    return this.cubitData.hasGrading
  }
}

export enum ValidLibraryObjectActionState {
  enroll = 'enroll',
  availableOn = 'availableOn',
  joinGroup = 'joinGroup',
  scheduleSession = 'scheduleSession',
  completeQuiz = 'completeQuiz',
  sessionScheduled = 'sessionScheduled',
  pendingGrouping = 'pendingGrouping',
  pendingScheduling = 'pendingScheduling',
  joinSession = 'joinSession',
  startSession = 'startSession',
  viewResults = 'viewResults',
  completePeerReviews = 'completePeerReviews',
  experienceExpired = 'experienceExpired',
  loading = 'loading',
  suspended = 'suspended',
  canceled = 'canceled',
  joinNewGroup = 'joinNewGroup',
}
